import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    ThresholdConfigSensor,
    UpdateThresholdsRequest,
    useUpdateThresholdsMutation,
} from 'business-dashboard/src/api/thresholdsApi';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import { analyticsLogger } from 'commons/src/analytics';
import { CUSTOM_THRESHOLD_SAVED } from 'commons/src/analytics/AnalyticsEvents';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ErrorType } from 'commons/src/models/commonTypeScript';
import { ThresholdOption, SpaceThresholds } from 'commons/src/models/customThresholdModels';
import { toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import { thresholdOrder } from '../../components/thresholds/useThresholdSetting';

export type UseDeviceThresholdMgmtProps = {
    sensorType: SensorTypes;
    thresholds: SpaceThresholds;
    serialNumber: string;
    updateSuccessAction?: () => void;
};

export type UseDeviceThresholdMgmtReturn = {
    thresholdOptions: ThresholdOption[];
    selectedValues: number[];
    handleSetSelectedValues: (values: number[]) => void;
    handleOnSave: (sensorValue: ThresholdConfigSensor) => Promise<void>;
    isLoading: boolean;
    isSaved: boolean;
    error?: ErrorType;
};

export default function useDeviceThresholdMgmt({
    thresholds,
    serialNumber,
    updateSuccessAction,
}: UseDeviceThresholdMgmtProps): UseDeviceThresholdMgmtReturn {
    const dispatch = useDispatch();
    const [updateThresholds, { isLoading }] = useUpdateThresholdsMutation();
    const [error, setError] = useState<ErrorType | undefined>(undefined);
    const [isSaved, setIsSaved] = useState(false);
    const [selectedValues, setSelectedValues] = useState<number[]>([]);

    const thresholdOptions = useMemo(() => {
        const opts = thresholds.thresholdOptions?.options?.[ThresholdsResourceScope.DEVICE] ?? [];
        return [...opts].sort((a, b) => thresholdOrder.indexOf(a.type) - thresholdOrder.indexOf(b.type));
    }, [thresholds.thresholdOptions]);

    useEffect(() => {
        const opts = thresholds.thresholdOptions?.options?.[ThresholdsResourceScope.DEVICE] ?? [];
        const sorted =
            opts.length > 0
                ? [...opts].sort((a, b) => thresholdOrder.indexOf(a.type) - thresholdOrder.indexOf(b.type))
                : [];
        if (thresholds.ranges?.length && sorted.length > 0) {
            const values = sorted.map((opt, i) => thresholds.ranges[i]?.to ?? thresholds.ranges[i]?.from ?? opt.value);
            setSelectedValues(values);
        }
    }, [thresholds.ranges, thresholds.thresholdOptions]);

    const logThresholdSaved = (request: UpdateThresholdsRequest): void => {
        analyticsLogger(CUSTOM_THRESHOLD_SAVED, {
            resourceScope: request.resourceScope,
            id: request.id,
            sensors: request.sensors.map(sensor => ({
                sensor: sensor.sensor,
                thresholds: sensor.thresholds.map(t => ({
                    thresholdType: t.thresholdType,
                    value: t.value,
                })),
            })),
        });
    };

    const handleOnSave = async (sensorValue: ThresholdConfigSensor): Promise<void> => {
        setError(undefined);
        const request = {
            resourceScope: ThresholdsResourceScope.DEVICE,
            id: serialNumber,
            sensors: [{ sensor: sensorValue.sensor, thresholds: sensorValue.thresholds }],
        };
        logThresholdSaved(request);
        try {
            await updateThresholds(request).unwrap();
            setIsSaved(true);
            dispatch(displayAlertBoxSagaAction('CustomThresholds.UpdateSuccess', false, true));
            updateSuccessAction?.();
        } catch (err) {
            dispatch(displayAlertBoxSagaAction(toErrorTypeWithMessage(err).message, true, true));
        }
    };

    const handleSetSelectedValues = (values: number[]): void => {
        setSelectedValues(values);
        if (isSaved) setIsSaved(false);
    };

    return {
        thresholdOptions,
        selectedValues,
        handleSetSelectedValues,
        handleOnSave,
        isLoading,
        isSaved,
        error,
    };
}
